import { useEffect, useState } from "react";
import * as firebaseui from "firebaseui";
import { auth } from "../../config/firebase-config";
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import "firebaseui/dist/firebaseui.css";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

const Register = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const uiConfig: firebaseui.auth.Config = {
      signInSuccessUrl: "/authenticated",
      signInFlow: "popup",
      signInOptions: [GoogleAuthProvider.PROVIDER_ID],
    };
    const ui =
      firebaseui.auth.AuthUI.getInstance() || new firebaseui.auth.AuthUI(auth);

    ui.start("#firebaseui-auth-container", uiConfig);
  }, []);

  const signUp = async (e: any) => {
    e.preventDefault();

    // Check if passwords match
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    try {
      // Create Firebase account
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      if (userCredential.user) {
        navigate("/authenticated");
      }
    } catch (e: any) {
      console.error(e);
      if (e.code === "auth/email-already-in-use") {
        toast.error("Email already in use");
      } else if (e.code === "auth/weak-password") {
        toast.error("Password is too weak");
      } else {
        toast.error(e.message || "Registration failed. Please try again.");
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-amber-50">
      {/* Navigation remains the same - place your NavigationBar component here */}

      <div className="flex flex-col items-center justify-center px-4 pt-16 pb-32">
        <div className="w-full max-w-lg bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header section */}
          <div className="px-8 pt-8 pb-2 text-center">
            <h1 className="text-3xl font-bold text-gray-800">
              Create an account
            </h1>
            <p className="mt-2 text-gray-600">
              Join Yippity to create and share interactive quizzes
            </p>
          </div>

          {/* Form section */}
          <div className="px-8 py-6">
            <form onSubmit={signUp}>
              <div className="mb-4">
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 text-gray-800 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                />
              </div>

              <div className="mb-4">
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 text-gray-800 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Must be at least 6 characters
                </p>
              </div>

              <div className="mb-6">
                <input
                  type="password"
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 text-gray-800 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 px-4 bg-gradient-to-r from-yellow-500 to-amber-500 text-white font-medium rounded-lg shadow-md hover:from-yellow-600 hover:to-amber-600 transition-all duration-200"
                disabled={
                  !(
                    email.length &&
                    password.length &&
                    password === confirmPassword
                  )
                }
              >
                Sign up
              </button>
            </form>

            {/* Divider */}
            <div className="my-6 flex items-center">
              <div className="flex-grow border-t border-gray-300"></div>
              <span className="mx-4 text-gray-600 font-medium">OR</span>
              <div className="flex-grow border-t border-gray-300"></div>
            </div>

            {/* Firebase UI container */}
            <div id="firebaseui-auth-container" className="w-full"></div>

            {/* Sign in link */}
            <div className="mt-6 text-center">
              <p className="text-gray-600">
                Already have an account?{" "}
                <a
                  href="/login"
                  className="text-yellow-600 hover:text-yellow-700 font-medium"
                >
                  Sign in
                </a>
              </p>
            </div>
          </div>

          {/* Footer gradient section */}
          <div className="bg-gradient-to-r from-yellow-500 to-amber-500 p-8">
            <div className="text-white text-center">
              <h3 className="text-xl font-bold mb-2">Yippity Quiz Platform</h3>
              <p className="opacity-80">
                Create, share, and take quizzes with intelligent feedback.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
